import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  flattenPlannedWorkouts,
  getPlanEndDateISO,
  getPlanStartDateISO,
  getPlanWorkoutsCompletion,
  isPlanClosedStatus,
  isPlanEnded,
  toIsoDay
} from '../../utils/plan_progress';

function pickLastCompleted(workouts, todayIso) {
  return (
    workouts
      .filter((w) => w?.date && String(w.type || '').toLowerCase() !== 'rest')
      .filter((w) => w?.completed === true)
      .filter((w) => String(w.date) <= String(todayIso))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)))[0] || null
  );
}

function pickNextWorkout(workouts, todayIso) {
  return (
    workouts
      .filter((w) => w?.date && String(w.type || '').toLowerCase() !== 'rest')
      .filter((w) => w?.completed !== true)
      .filter((w) => String(w.date) > String(todayIso))
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))[0] || null
  );
}

function OneLiner({ label, workout }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <div className="font-bold text-gray-400 uppercase tracking-wider text-[10px] w-16 shrink-0">{label}</div>
      <div className="min-w-0 flex-1 text-right truncate">
        {workout ? (
          <div className="truncate">
            <span className="font-bold text-gray-900">{workout.title || '(Untitled)'}</span>
            <span className="text-gray-300 mx-1.5">|</span>
            <span className="font-mono text-gray-500 font-medium">{workout.date}</span>
          </div>
        ) : (
          <span className="text-gray-300 font-medium">—</span>
        )}
      </div>
    </div>
  );
}

export default function PlanHomeCard({ planSummary }) {
  const { t } = useTranslation();
  const todayIso = new Date().toISOString().split('T')[0];

  const computed = useMemo(() => {
    const planJson = planSummary?.plan;
    const all = flattenPlannedWorkouts(planJson);
    const weeksCount = Array.isArray(planJson?.weeks) ? planJson.weeks.length : 0;

    const start = getPlanStartDateISO(planJson, planSummary?.start_date) || null;
    const end = getPlanEndDateISO(planJson) || null;

    const { pct, done, total } = getPlanWorkoutsCompletion(planJson);
    const last = pickLastCompleted(all, todayIso);
    const next = pickNextWorkout(all, todayIso);
    const ended = isPlanEnded(end, new Date());

    return { weeksCount, start, end, pct, done, total, last, next, ended };
  }, [planSummary, todayIso]);

  const title = planSummary?.title || t('plan.generation.untitled');
  const status = String(planSummary?.status || '');
  const isGenerating = status === 'generating' || status.startsWith('generating_');
  const isClosed = isPlanClosedStatus(status);

  return (
    <Link
      to={`/plans/${planSummary?.id}`}
      className="block bg-white border border-gray-100 rounded-3xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_24px_rgba(99,102,241,0.15)] hover:-translate-y-1 transition-all duration-300 p-5 md:p-6 group relative overflow-hidden"
      aria-label={title}
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

      <div className="relative z-10">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-xl font-bold text-gray-900 truncate tracking-tight">{title}</div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 font-medium">
              <span className="flex items-center gap-1.5">
                <span className="uppercase tracking-wider text-[10px] font-bold text-gray-400">{t('home.plan_card.start')}</span>
                <span className="font-mono text-gray-700">{computed.start || '—'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="uppercase tracking-wider text-[10px] font-bold text-gray-400">{t('home.plan_card.end')}</span>
                <span className="font-mono text-gray-700">{computed.end || '—'}</span>
              </span>
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2">
            {computed.weeksCount > 0 ? (
                <div className="px-2.5 py-1 rounded-full bg-gray-50 border border-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wide">
                  {computed.weeksCount} {t('home.plan_card.weeks')}
                </div>
            ) : null}
            {status === 'completed' ? (
              <div className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wide">
                {t('home.plan_card.completed')}
              </div>
            ) : null}
            {status === 'stopped' ? (
              <div className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wide">
                {t('home.plan_card.stopped')}
              </div>
            ) : null}
            {computed.ended && !isClosed ? (
              <div className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wide">
                {t('home.plan_card.ended')}
              </div>
            ) : null}
            {isGenerating ? (
              <div className="px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 text-[10px] font-bold uppercase tracking-wide animate-pulse">
                {t('home.plan_card.generating')}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-end justify-between gap-3 mb-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('home.plan_card.completion')}</div>
            <div className="text-sm font-bold tabular-nums text-gray-900">
              {computed.pct}% <span className="text-gray-400 font-medium text-xs ml-1">({computed.done}/{computed.total})</span>
            </div>
          </div>
          <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-1000 ease-out" style={{ width: `${computed.pct}%` }} />
          </div>
        </div>

        <div className="mt-6 space-y-3 pt-4 border-t border-gray-50">
          <OneLiner label={t('home.plan_card.last_workout')} workout={computed.last ? { ...computed.last, date: toIsoDay(computed.last.date) } : null} />
          <OneLiner label={t('home.plan_card.next_workout')} workout={isClosed ? null : (computed.next ? { ...computed.next, date: toIsoDay(computed.next.date) } : null)} />
        </div>
      </div>
    </Link>
  );
}

